import React from "react";
import { Card, CardBody, CardHeader } from "./ui/card";
import { Typography, Chip, Collapse } from "./ui";
import { ResourceGroup } from "@/models/resource-group";
import { FaFolder } from "react-icons/fa";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

interface ResourceGroupCardProps {
  group: ResourceGroup;
  onClick?: () => void;
  className?: string;
  resourceCount?: number;
  isExpanded?: boolean;
  onToggle?: () => void;
  viewMode?: string;
  viewSize?: string;
  level?: number;
  children?: React.ReactNode;
}

const sizeStyles = {
  small: {
    header: "py-1.5 px-2.5",
    iconSize: "text-sm" as const,
    chevronSize: "xs" as const,
    textVariant: "text-sm" as const,
    showDescription: false,
    childContainer: "ml-3 pl-3 py-2 pr-2 mt-1.5",
  },
  medium: {
    header: "py-3 px-4",
    iconSize: "text-base" as const,
    chevronSize: "sm" as const,
    textVariant: "text-base" as const,
    showDescription: false,
    childContainer: "ml-4 pl-4 py-3 pr-3 mt-2",
  },
  large: {
    header: "py-4 px-5",
    iconSize: "text-lg" as const,
    chevronSize: "sm" as const,
    textVariant: "text-base" as const,
    showDescription: true,
    childContainer: "ml-4 pl-4 py-3 pr-3 mt-2",
  },
};

const ResourceGroupCard: React.FC<ResourceGroupCardProps> = ({
  group,
  onClick,
  className = "",
  resourceCount,
  isExpanded,
  onToggle,
  viewMode,
  viewSize = "medium",
  level,
  children,
}) => {
  const folderColor = group.color || "#6366F1"; // arc accent (indigo) default

  if (viewMode === "grid") {
    const styles = sizeStyles[viewSize as keyof typeof sizeStyles] || sizeStyles.medium;

    return (
      <div style={{ gridColumn: "1 / -1" }} className={className}>
        {/* Folder header bar */}
        <div
          className={`flex items-center gap-3 ${styles.header} bg-surface-secondary rounded-arc-lg cursor-pointer hover:bg-surface-tertiary transition-colors duration-arc-fast border border-arc-border`}
          onClick={onToggle || onClick}
        >
          <FontAwesomeIcon
            icon={isExpanded ? faChevronDown : faChevronRight}
            className="text-content-secondary"
            size={styles.chevronSize}
          />
          <FaFolder className={styles.iconSize} style={{ color: folderColor }} />
          <Typography
            variant="h6"
            className={`${styles.textVariant} font-semibold text-content-primary flex-1`}
          >
            {group.name || "Untitled Folder"}
          </Typography>
          {styles.showDescription && group.description && (
            <Typography variant="text-sm" color="secondary" className="flex-shrink truncate max-w-xs">
              {group.description}
            </Typography>
          )}
          {resourceCount !== undefined && (
            <Chip
              label={String(resourceCount)}
              size="sm"
              className="bg-surface-tertiary text-content-primary font-medium"
            />
          )}
        </div>

        {/* Expanded children container */}
        {children && (
          <Collapse in={isExpanded}>
            <div
              className={`border-l-2 bg-surface-secondary/20 rounded-r-arc-md ${styles.childContainer}`}
              style={{ borderLeftColor: `${folderColor}40` }}
            >
              {children}
            </div>
          </Collapse>
        )}
      </div>
    );
  }

  if (viewMode === "list") {
    const styles = sizeStyles[viewSize as keyof typeof sizeStyles] || sizeStyles.medium;

    return (
      <div className={className} style={{ marginLeft: level ? level * 16 : undefined }}>
        <div
          className={`flex items-center gap-3 ${styles.header} bg-surface-secondary rounded-arc-lg cursor-pointer hover:bg-surface-tertiary transition-colors duration-arc-fast border border-arc-border`}
          onClick={onToggle || onClick}
        >
          <FontAwesomeIcon
            icon={isExpanded ? faChevronDown : faChevronRight}
            className="text-content-secondary"
            size={styles.chevronSize}
          />
          <FaFolder className={styles.iconSize} style={{ color: folderColor }} />
          <Typography
            variant="h6"
            className={`${styles.textVariant} font-semibold text-content-primary flex-1`}
          >
            {group.name || "Untitled Folder"}
          </Typography>
          {styles.showDescription && group.description && (
            <Typography variant="text-sm" color="secondary" className="flex-shrink truncate max-w-xs">
              {group.description}
            </Typography>
          )}
          {resourceCount !== undefined && (
            <Chip
              label={String(resourceCount)}
              size="sm"
              className="bg-surface-tertiary text-content-primary font-medium"
            />
          )}
        </div>

        {children && (
          <Collapse in={isExpanded}>
            <div
              className={`border-l-2 bg-surface-secondary/20 rounded-r-arc-md ${styles.childContainer}`}
              style={{ borderLeftColor: `${folderColor}40` }}
            >
              {children}
            </div>
          </Collapse>
        )}
      </div>
    );
  }

  if (viewMode === "board") {
    // Cap effective size at medium for narrow kanban columns
    const effectiveSize = viewSize === "large" ? "medium" : (viewSize as keyof typeof sizeStyles);
    const styles = sizeStyles[effectiveSize] || sizeStyles.medium;

    return (
      <div className={className} style={{ marginLeft: level ? level * 12 : undefined }}>
        <div
          className={`flex items-center gap-2 ${styles.header} bg-surface-secondary rounded-arc-lg cursor-pointer hover:bg-surface-tertiary transition-colors duration-arc-fast border border-arc-border`}
          onClick={onToggle || onClick}
        >
          <FontAwesomeIcon
            icon={isExpanded ? faChevronDown : faChevronRight}
            className="text-content-secondary"
            size={styles.chevronSize}
          />
          <FaFolder className={styles.iconSize} style={{ color: folderColor }} />
          <Typography
            variant="h6"
            className={`${styles.textVariant} font-semibold text-content-primary flex-1 truncate`}
          >
            {group.name || "Untitled Folder"}
          </Typography>
          {resourceCount !== undefined && (
            <Chip
              label={String(resourceCount)}
              size="sm"
              className="bg-surface-tertiary text-content-primary font-medium"
            />
          )}
        </div>

        {children && (
          <Collapse in={isExpanded}>
            <div
              className={`border-l-2 bg-surface-secondary/20 rounded-r-arc-md ${styles.childContainer}`}
              style={{ borderLeftColor: `${folderColor}40` }}
            >
              {children}
            </div>
          </Collapse>
        )}
      </div>
    );
  }

  // Fallback: Card-based rendering for unknown viewModes
  return (
    <Card
      className={`cursor-pointer hover:shadow-md transition-shadow ${className}`}
      onClick={onToggle || onClick}
    >
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <FaFolder className="text-content-secondary" />
          <Typography variant="text-base" className="font-medium">
            {group.name || "Untitled Folder"}
          </Typography>
        </div>
      </CardHeader>
      <CardBody className="pt-2">
        {group.description && (
          <Typography variant="text-sm" color="secondary">
            {group.description}
          </Typography>
        )}
      </CardBody>
      {children}
    </Card>
  );
};

export default ResourceGroupCard;
